import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Menu,
  Search,
  Bell,
  Settings,
  ChevronDown,
  LayoutDashboard,
  CreditCard,
  Play,
  MessageSquare,
  Smartphone,
  FileText,
  Users,
  ClipboardList,
  Settings2,
  User,
  LogOut,
  Moon,
  Sun,
  Wallet,
  BellRing,
  QrCode,
  ShoppingCart,
  Monitor,
  Package,
  Building2,
  Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Random greeting messages
const greetingMessages = [
  { text: "Hey {name}, great to see you!", emoji: "👋" },
  { text: "Welcome back, {name}!", emoji: "🎉" },
  { text: "Good to have you here, {name}!", emoji: "✨" },
  { text: "Hey {name}, ready to crush it today?", emoji: "💪" },
  { text: "Hi {name}, hope you're having a great day!", emoji: "☀️" },
  { text: "{name}, welcome aboard!", emoji: "🚀" },
  { text: "Great to see you again, {name}!", emoji: "😊" },
  { text: "Hey there, {name}! Let's do this!", emoji: "🔥" },
  { text: "{name}, you're looking awesome today!", emoji: "⭐" },
  { text: "Welcome, {name}! Ready for success?", emoji: "🏆" },
];

export const DashboardLayout = () => {
  const { user, merchant, staff, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null); // null = All Branches
  const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const lastNotificationIdRef = useRef(null);

  // Fetch branches
  const fetchBranches = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/merchant/branches`, { withCredentials: true });
      setBranches(response.data);
      
      // If staff is assigned to a branch, auto-select it
      if (staff?.branch_id) {
        const assignedBranch = response.data.find(b => b.branch_id === staff.branch_id);
        if (assignedBranch) {
          setSelectedBranch(assignedBranch);
        }
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  }, [staff?.branch_id]);

  useEffect(() => {
    if (merchant) {
      fetchBranches();
    }
  }, [merchant, fetchBranches]);

  // Real-time notification polling
  const fetchNotifications = useCallback(async () => {
    if (!merchant) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/notifications`, { 
        withCredentials: true,
        params: { limit: 10 }
      });
      
      const newNotifications = response.data.notifications || [];
      setNotifications(newNotifications);
      setUnreadCount(response.data.unread_count || 0);
      
      // Show toast for new notifications
      if (newNotifications.length > 0 && lastNotificationIdRef.current) {
        const latestId = newNotifications[0]?.notification_id;
        if (latestId && latestId !== lastNotificationIdRef.current) {
          // Find new notifications since last check
          const lastIndex = newNotifications.findIndex(n => n.notification_id === lastNotificationIdRef.current);
          const newOnes = lastIndex > 0 ? newNotifications.slice(0, lastIndex) : (latestId !== lastNotificationIdRef.current ? [newNotifications[0]] : []);
          
          newOnes.forEach(notification => {
            const type = notification.type || 'info';
            const message = notification.message || 'New notification';
            
            if (type === 'payment_received' || type === 'transaction') {
              toast.success(message, {
                description: notification.description,
                duration: 5000,
              });
            } else if (type === 'error' || type === 'alert') {
              toast.error(message, {
                description: notification.description,
                duration: 5000,
              });
            } else {
              toast.info(message, {
                description: notification.description,
                duration: 4000,
              });
            }
          });
        }
      }
      
      // Update last notification ID
      if (newNotifications.length > 0) {
        lastNotificationIdRef.current = newNotifications[0]?.notification_id;
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [merchant]);

  // Poll notifications every 10 seconds
  useEffect(() => {
    if (merchant) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 10000);
      return () => clearInterval(interval);
    }
  }, [merchant, fetchNotifications]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API_URL}/api/notifications/${notificationId}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/mark-all-read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Role-based permissions map
  const ROLE_PERMISSIONS = {
    owner: {
      manage_staff: true,
      manage_products: true,
      view_reports: true,
      export_reports: true,
      configure_settings: true,
      manage_transactions: true,
      access_pos: true,
      create_transactions: true,
      confirm_payments: true,
      view_transactions: true,
      view_audit_logs: true,
      manage_payment_sources: true,
      view_dashboard: true,
      view_qr_payments: true,
    },
    manager: {
      manage_staff: false,
      manage_products: true,
      view_reports: true,
      export_reports: true,
      configure_settings: false,
      manage_transactions: true,
      access_pos: true,
      create_transactions: true,
      confirm_payments: true,
      view_transactions: true,
      view_audit_logs: true,
      manage_payment_sources: true,
      view_dashboard: true,
      view_qr_payments: true,
    },
    cashier: {
      manage_staff: false,
      manage_products: false,
      view_reports: false,
      export_reports: false,
      configure_settings: false,
      manage_transactions: false,
      access_pos: true,
      create_transactions: true,
      confirm_payments: true,
      view_transactions: true,
      view_audit_logs: false,
      manage_payment_sources: false,
      view_dashboard: true,
      view_qr_payments: true,
    },
    accountant: {
      manage_staff: false,
      manage_products: false,
      view_reports: true,
      export_reports: true,
      configure_settings: false,
      manage_transactions: false,
      access_pos: false,
      create_transactions: false,
      confirm_payments: false,
      view_transactions: true,
      view_audit_logs: true,
      manage_payment_sources: false,
      view_dashboard: true,
      view_qr_payments: false,
    },
  };

  // Get current user's permissions
  const userPermissions = ROLE_PERMISSIONS[staff?.role] || {};

  // Navigation items with permission requirements
  const navigation = [
    { name: 'Dashboard', href: '/app/dashboard', icon: LayoutDashboard, permission: 'view_dashboard' },
    { name: 'POS Checkout', href: '/app/pos', icon: Monitor, permission: 'access_pos' },
    { name: 'Checkout', href: '/app/checkout', icon: ShoppingCart, permission: 'create_transactions' },
    { name: 'Products', href: '/app/products', icon: Package, permission: 'manage_products' },
    { name: 'QR Payments', href: '/app/qr-payments', icon: QrCode, permission: 'view_qr_payments' },
    { name: 'Transactions', href: '/app/transactions', icon: CreditCard, permission: 'view_transactions' },
    { name: 'New Transaction', href: '/app/transactions/new', icon: Play, permission: 'create_transactions' },
    { name: 'Payment Sources', href: '/app/payment-sources', icon: Wallet, permission: 'manage_payment_sources' },
    { name: 'Branches', href: '/app/branches', icon: Building2, permission: 'manage_staff' },
    { name: 'Notifications', href: '/app/notifications', icon: BellRing, permission: 'view_dashboard' },
    { name: 'Reports', href: '/app/reports', icon: FileText, permission: 'view_reports' },
    { name: 'Staff', href: '/app/staff', icon: Users, permission: 'manage_staff' },
    { name: 'Audit Log', href: '/app/audit', icon: ClipboardList, permission: 'view_audit_logs' },
  ];

  // Filter navigation based on user permissions
  const filteredNav = navigation.filter(item => {
    if (!item.permission) return true;
    return userPermissions[item.permission] === true;
  });

  const isDashboardPage = location.pathname === '/app/dashboard';

  const userName = staff?.name || user?.name || 'Test Admin';
  const userEmail = user?.email || 'admin@test.com';

  // Random greeting - selected once per session/mount
  const greeting = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * greetingMessages.length);
    return greetingMessages[randomIndex];
  }, []);

  const greetingText = greeting.text.replace('{name}', userName);

  return (
    <div 
      className="h-screen w-screen overflow-hidden"
      style={{ 
        display: 'grid',
        gridTemplateColumns: isDashboardPage ? '260px 1fr 360px' : '260px 1fr',
        gap: '20px',
        padding: '24px',
        backgroundColor: isDarkMode ? '#0f172a' : '#F5F7FA',
        fontFamily: 'Inter, sans-serif',
        transition: 'background-color 0.3s ease'
      }}
      data-testid="dashboard-layout"
    >
      {/* Animation Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        
        .animate-slideIn {
          animation: slideIn 0.3s ease-out forwards;
        }
        
        .hover-lift {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .hover-lift:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px -4px rgba(0, 0, 0, 0.1);
        }
        
        .hover-scale {
          transition: transform 0.2s ease;
        }
        
        .hover-scale:hover {
          transform: scale(1.05);
        }
        
        .nav-item {
          transition: all 0.3s ease;
        }
        
        .nav-item:hover .nav-icon {
          transform: scale(1.1);
        }
        
        .nav-item:hover .nav-text {
          transform: translateX(4px);
        }
        
        .nav-icon, .nav-text {
          transition: transform 0.3s ease;
        }
      `}</style>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* LEFT SIDEBAR - Modern Fintech Design */}
      <aside 
        className="rounded-xl overflow-hidden flex flex-col"
        style={{ 
          background: 'linear-gradient(180deg, #1C3557 0%, #17324F 33%, #152E49 66%, #12273F 100%)',
          height: 'calc(100vh - 48px)',
          width: '260px'
        }}
        data-testid="sidebar"
      >
        {/* FlowLedger Branding */}
        <div 
          className="p-4 text-center"
        >
          <p 
            style={{ 
              color: '#FFFFFF', 
              fontSize: '26px', 
              fontWeight: 700,
              lineHeight: '1.2'
            }}
          >
            FlowLedger
          </p>
          <p 
            style={{ 
              color: '#14B8A6', 
              fontSize: '12px',
              fontWeight: 500,
              marginTop: '4px'
            }}
          >
            TrustBridge Business Portal
          </p>
          <p 
            style={{ 
              color: '#60A5FA', 
              fontSize: '12px',
              fontWeight: 500,
              marginTop: '4px'
            }}
          >
            {merchant?.business_name || 'My Business'}
          </p>
        </div>

        {/* Navigation List */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {filteredNav.map((item, index) => {
              const active = isActive(item.href);
              const IconComponent = item.icon;
              
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center nav-item group"
                  style={{
                    padding: '12px 18px',
                    gap: '12px',
                    borderRadius: '8px',
                    backgroundColor: active ? '#1F5F73' : 'transparent',
                    transition: 'all 0.3s ease',
                    animationDelay: `${index * 30}ms`
                  }}
                  onMouseEnter={(e) => {
                    if (!active) {
                      e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
                      e.currentTarget.style.transform = 'translateX(4px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }
                  }}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  {/* Icon Container */}
                  <div 
                    className="flex items-center justify-center flex-shrink-0 nav-icon"
                    style={{
                      width: active ? '28px' : 'auto',
                      height: active ? '28px' : 'auto',
                      borderRadius: '6px',
                      backgroundColor: active ? '#2FD1C7' : 'transparent',
                      padding: active ? '4px' : '0',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <IconComponent 
                      style={{ 
                        color: active ? '#FFFFFF' : '#B6C6D6',
                        width: '20px',
                        height: '20px',
                        transition: 'color 0.3s ease'
                      }}
                      strokeWidth={1.8}
                      className="group-hover:text-teal-400"
                    />
                  </div>
                  {/* Text */}
                  <span 
                    className="nav-text"
                    style={{ 
                      fontSize: '15px', 
                      fontWeight: 500,
                      color: active ? '#FFFFFF' : '#E8F1F8',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {item.name}
                  </span>
                  {active && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Bottom Section - Profile & Settings */}
        <div 
          className="px-3 py-4"
          style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
        >
          {/* Theme Toggle */}
          <button 
            onClick={() => setIsDarkMode(!isDarkMode)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all mb-2 group"
            style={{ color: '#9FB4C9' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            data-testid="theme-toggle-btn"
          >
            {isDarkMode ? (
              <Sun className="w-5 h-5 transition-transform duration-300 group-hover:rotate-180" />
            ) : (
              <Moon className="w-5 h-5 transition-transform duration-300 group-hover:-rotate-12" />
            )}
            <span className="text-sm">{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>

          {/* Profile Row - Clickable */}
          <div 
            onClick={() => navigate('/app/profile')}
            className="flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer transition-all group"
            style={{ marginBottom: '8px' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
              e.currentTarget.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            data-testid="profile-card"
          >
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
              style={{ backgroundColor: '#14B8A6' }}
            >
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <User className="w-5 h-5 text-white" strokeWidth={2} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p 
                className="truncate"
                style={{ 
                  color: '#FFFFFF', 
                  fontSize: '15px', 
                  fontWeight: 600,
                  lineHeight: '1.3'
                }}
              >
                {userName}
              </p>
              <p 
                className="truncate"
                style={{ 
                  color: '#9FB4C9', 
                  fontSize: '13px',
                  fontWeight: 400
                }}
              >
                {userEmail}
              </p>
            </div>
            <ChevronDown className="w-4 h-4 text-slate-400 -rotate-90 transition-transform duration-300 group-hover:translate-x-1" />
          </div>
          
          {/* Settings & Logout Row */}
          <div className="flex items-center gap-2">
            <button 
              onClick={() => navigate('/app/settings')}
              className="flex-1 flex items-center justify-center gap-2 p-2 rounded-lg transition-all group"
              style={{ color: '#9FB4C9' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)';
                e.currentTarget.style.color = '#FFFFFF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#9FB4C9';
              }}
              data-testid="bottom-settings-btn"
            >
              <Settings2 className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90" strokeWidth={1.8} />
              <span className="text-sm">Settings</span>
            </button>
            
            <button 
              onClick={handleLogout}
              className="flex-1 flex items-center justify-center gap-2 p-2 rounded-lg transition-all group"
              style={{ color: '#9FB4C9' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.1)';
                e.currentTarget.style.color = '#EF4444';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#9FB4C9';
              }}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" strokeWidth={1.8} />
              <span className="text-sm">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main 
        className="flex flex-col overflow-hidden"
        style={{ height: 'calc(100vh - 48px)' }}
      >
        {/* Top Header - 70px */}
        <header 
          className="flex items-center justify-between px-6 flex-shrink-0 rounded-xl mb-4 transition-all duration-300"
          style={{ 
            height: '70px',
            boxShadow: '0px 6px 18px rgba(0,0,0,0.06)',
            backgroundColor: isDarkMode ? '#1e293b' : '#FFFFFF'
          }}
          data-testid="header"
        >
          {/* Mobile menu button */}
          <button
            className={`lg:hidden p-2 rounded-lg ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`} />
          </button>

          {/* Welcome Message */}
          <div className="hidden lg:block">
            <p 
              style={{ 
                fontSize: '18px', 
                fontWeight: 600,
                color: isDarkMode ? '#FFFFFF' : '#111827'
              }}
            >
              {greetingText.split(userName)[0]}
              <span style={{ color: '#14B8A6' }}>{userName}</span>
              {greetingText.split(userName)[1]} {greeting.emoji}
            </p>
          </div>

          {/* Branch Selector */}
          {branches.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setBranchDropdownOpen(!branchDropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                  isDarkMode 
                    ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' 
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
                data-testid="branch-selector"
              >
                <Building2 className="w-4 h-4 text-teal-500" />
                <span className="text-sm font-medium max-w-[150px] truncate">
                  {selectedBranch ? selectedBranch.branch_name : 'All Branches'}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${branchDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {branchDropdownOpen && (
                <div 
                  className={`absolute top-full left-0 mt-2 w-56 rounded-lg shadow-lg border z-50 ${
                    isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="p-1">
                    {/* All Branches option - only for owners/managers */}
                    {(staff?.role === 'owner' || staff?.role === 'manager') && (
                      <button
                        onClick={() => { setSelectedBranch(null); setBranchDropdownOpen(false); }}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                          !selectedBranch
                            ? isDarkMode ? 'bg-teal-900/50 text-teal-300' : 'bg-teal-50 text-teal-700'
                            : isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Building2 className="w-4 h-4" />
                        <span>All Branches</span>
                        {!selectedBranch && <Check className="w-4 h-4 ml-auto" />}
                      </button>
                    )}
                    
                    {/* Individual branches */}
                    {branches.map((branch) => (
                      <button
                        key={branch.branch_id}
                        onClick={() => { setSelectedBranch(branch); setBranchDropdownOpen(false); }}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                          selectedBranch?.branch_id === branch.branch_id
                            ? isDarkMode ? 'bg-teal-900/50 text-teal-300' : 'bg-teal-50 text-teal-700'
                            : isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Building2 className="w-4 h-4" />
                        <div className="flex-1 min-w-0">
                          <span className="block truncate">{branch.branch_name}</span>
                          <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                            {branch.city || branch.branch_code}
                          </span>
                        </div>
                        {selectedBranch?.branch_id === branch.branch_id && <Check className="w-4 h-4" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Currency Toggle */}
          <CurrencyToggle isDarkMode={isDarkMode} />

          {/* Right side icons */}
          <div className="flex items-center gap-3">
            {/* Notifications Dropdown */}
            <div className="relative">
              <button 
                onClick={() => setNotificationDropdownOpen(!notificationDropdownOpen)}
                className={`p-2 rounded-lg relative transition-all duration-200 hover:scale-110 ${isDarkMode ? 'hover:bg-slate-700 text-gray-300' : 'hover:bg-gray-100 text-gray-500'}`}
                data-testid="notifications-btn"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              
              {/* Notification Dropdown Panel */}
              {notificationDropdownOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setNotificationDropdownOpen(false)}
                  />
                  <div className={`absolute right-0 top-12 w-80 rounded-xl shadow-2xl border z-50 overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                    <div className={`flex items-center justify-between p-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                      <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Notifications</h3>
                      {unreadCount > 0 && (
                        <button 
                          onClick={markAllAsRead}
                          className="text-xs text-teal-500 hover:text-teal-600 font-medium"
                        >
                          Mark all read
                        </button>
                      )}
                    </div>
                    
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className={`p-6 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <BellRing className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No notifications yet</p>
                        </div>
                      ) : (
                        notifications.map((notification) => (
                          <div 
                            key={notification.notification_id}
                            onClick={() => !notification.read && markAsRead(notification.notification_id)}
                            className={`p-3 border-b cursor-pointer transition-colors ${
                              isDarkMode 
                                ? `border-slate-700 ${notification.read ? 'bg-slate-800' : 'bg-slate-700/50'}` 
                                : `border-gray-50 ${notification.read ? 'bg-white' : 'bg-teal-50/50'}`
                            } hover:${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                                notification.read ? 'bg-gray-300' : 
                                notification.type === 'payment_received' ? 'bg-green-500' :
                                notification.type === 'error' ? 'bg-red-500' : 'bg-teal-500'
                              }`} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {notification.message}
                                </p>
                                {notification.description && (
                                  <p className={`text-xs mt-0.5 truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {notification.description}
                                  </p>
                                )}
                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {new Date(notification.created_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    
                    <div className={`p-2 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                      <button 
                        onClick={() => {
                          setNotificationDropdownOpen(false);
                          navigate('/app/notifications');
                        }}
                        className={`w-full text-center text-sm py-2 rounded-lg transition-colors ${
                          isDarkMode ? 'text-teal-400 hover:bg-slate-700' : 'text-teal-600 hover:bg-gray-50'
                        }`}
                      >
                        View all notifications
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <button 
              onClick={() => navigate('/app/settings')}
              className={`p-2 rounded-lg transition-all duration-200 hover:scale-110 hover:rotate-90 ${isDarkMode ? 'hover:bg-slate-700 text-gray-300' : 'hover:bg-gray-100 text-gray-500'}`}
              data-testid="header-settings-btn"
            >
              <Settings className="w-5 h-5" />
            </button>
            <div 
              onClick={() => navigate('/app/profile')}
              className="w-9 h-9 rounded-full flex items-center justify-center ml-1 cursor-pointer transition-transform duration-200 hover:scale-110"
              style={{ backgroundColor: '#14B8A6' }}
            >
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
              ) : (
                <User className="w-5 h-5 text-white" strokeWidth={2} />
              )}
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <div className="flex-1 overflow-hidden">
          <Outlet context={{ isDarkMode, selectedBranch, branches }} />
        </div>
      </main>

      {/* RIGHT INFO PANEL - Only shown on dashboard page */}
      {isDashboardPage && (
        <aside 
          className="flex flex-col overflow-hidden"
          style={{ 
            height: 'calc(100vh - 48px)',
            gap: '16px'
          }}
          data-testid="right-panel"
          id="right-panel-container"
        >
          {/* This will be populated by Dashboard component via portal */}
        </aside>
      )}
    </div>
  );
};

export default DashboardLayout;
